const controlChars = Array.from({ length: 32 }, (_, i) => String.fromCharCode(i))
  // Экранируем спецсимволы, чтобы они корректно легли в строку регулярки
  .map(c => '\\u' + ('0000' + c.charCodeAt(0).toString(16)).slice(-4))
  .join('')

export default {
  /**
  * Нормализация имен файлов и папок
  * @mixin
  * @alias normalizes
  */
  methods: {
  /**
   * Нормализация имени папки
   * @param {string} dirName - Входящее имя папки
   * @param {string[]} existingDirs - Массив имен папок для проверки дублей
   * @param {number} maxLength - Максимальная длина базового имени
   * @returns {string}
   */
    $_normalizeDirectoryName(dirName, existingDirs = [], maxLength = 50) {
      if (!dirName) { return '_' }

      // 1. Обрезаем пробелы по бокам
      let name = dirName.trim()

      // 2. Заменяем недопустимые символы пути, пробелы и лапки на '_'
      // < > : " / \ | ? *
      const invalidPathAndSpaces = new RegExp('[<>:"/\\\\|?*\\s' + controlChars + ']', 'g')
      name = name.replace(invalidPathAndSpaces, '_')

      // 3. Схлопываем дубликаты подчёркиваний (___ -> _)
      name = name.replace(/_+/g, '_')

      // Обрезаем подчёркивания по краям (пробелы уже сведены к '_')
      name = name.replace(/^_+|_+$/g, '')

      // Если после очистки строка пустая, даем дефолтное имя
      if (!name) { name = '_' }

      // В Windows точка в конце имени, может вызвать ошибку
      name = name.replace(/\.+$/, '')

      // 4. Проверяем на системные имена Windows (con, prn...)
      const windowsReservedNames = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i
      if (windowsReservedNames.test(name)) {
        name = name + '_'
      }

      // Внутренняя функция генерации имени с учетом лимита maxLength символов
      const generateFinalDirName = (baseName, count) => {
        let suffix = ''
        if (count > 0) {
          suffix = `-${count}`
        }

        const maxBaseLength = maxLength - suffix.length
        let finalBase = baseName

        // Если не влезает в лимит, обрезаем по правилу: <перші maxLength>~<решта_довжини>
        if (baseName.length > maxBaseLength) {
          const part1 = baseName.substring(0, maxLength) // Первые maxLength символов оригинального базового имени
          const remainingLength = baseName.length - maxLength
          const tildePart = `~${remainingLength}`

          // Пересчитываем доступное место под "part1" с учетом хвоста tilde и суффикса дубликата
          const allowedPart1Length = maxLength - tildePart.length - suffix.length

          if (allowedPart1Length > 0) {
            finalBase = part1.substring(0, allowedPart1Length) + tildePart
          } else {
            finalBase = tildePart // Экстремальный случай, если суффикс занял всё место
          }
        }

        return finalBase + suffix
      }

      // 5. Дедупликация (Цикл для поиска уникального имени)
      let count = 0
      let result = generateFinalDirName(name, count)

      // Приводим к нижнему регистру для нечувствительной к регистру проверки (как в Windows)
      const existingLower = existingDirs.map(d => d.toLowerCase())

      while (existingLower.includes(result.toLowerCase())) {
        count++
        result = generateFinalDirName(name, count)
      }

      return result
    },
    /**
   * Нормализация имени файла с поддержкой составных расширений (.txt.sig, .tar.gz)
   * @param {string} fileName - Входящее имя файла
   * @param {string[]} existingFiles - Массив имен файлов для проверки дублей
   * @param {number} maxLength - Максимальная длина базового имени (без расширения)
   * @returns {string}
   */
    $_normalizeFileName(fileName, existingFiles = [], maxLength = 50) {
      if (!fileName) { return '_.txt' }

      let name = fileName.trim()
      let ext = ''

      // Регулярное выражение для поиска составных расширений на конце строки.
      // Оно ищет шаблоны вида .abc или .abc.xyz (длина каждого расширения от 1 до 5 символов)
      const multiExtMatch = name.match(/((?:\.[a-zA-Z0-9]{1,5}){1,2})$/)
    
      if (multiExtMatch) {
        ext = multiExtMatch[1] // Забирает полностью ".txt.sig" или ".pdf"
        name = name.substring(0, name.length - ext.length) // Остается чистое имя
      } else {
      // На всякий случай дефолтный фолбек, если расширение странное (например, .jpeg4)
        const lastDotIndex = name.lastIndexOf('.')
        if (lastDotIndex !== -1) {
          ext = name.substring(lastDotIndex)
          name = name.substring(0, lastDotIndex)
        }
      }

      // 1. Очистка базового имени
      // То же самое для файлов
      const invalidFileAndSpaces = new RegExp('[<>:"/\\\\|?*\\s' + controlChars + ']', 'g')
      name = name.replace(invalidFileAndSpaces, '_')
      name = name.replace(/_+/g, '_')
      // Обрезаем подчёркивания по краям (пробелы уже сведены к '_')
      name = name.replace(/^_+|_+$/g, '')
      if (!name) { name = '_' }

      // В Windows точка в конце имени, может вызвать ошибку
      name = name.replace(/\.+$/, '')

      // 2. Очистка расширения (удаляем запрещенные символы, если они туда пролезли)
      ext = ext.replace(invalidFileAndSpaces, '').replace(/_+/g, '_')

      // 3. Проверка на системные имена Windows (con, prn...)
      const windowsReservedNames = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])(\..*)?$/i
      if (windowsReservedNames.test(name + ext)) {
        name = name + '_'
      }

      // Внутренняя функция сборки финального имени файла
      const generateFinalFileName = (baseName, fileExt, count) => {
        let suffix = ''
        if (count > 0) {
          suffix = `-${count}`
        }

        const fullSuffix = suffix + fileExt // Например: "-1.txt.sig"
        const maxBaseLength = maxLength // Лимит maxLength символов строго для базового ИМЕНИ

        let finalBase = baseName

        // Если базовое имя само по себе длиннее maxLength символов
        if (baseName.length > maxBaseLength) {
          const part1 = baseName.substring(0, maxLength)
          const remainingLength = baseName.length - maxLength
          const tildePart = `~${remainingLength}`

          // Сколько символов от part1 мы можем оставить, чтобы имя (до суффикса) было ровно maxLength символов
          const allowedPart1Length = maxLength - tildePart.length - suffix.length

          if (allowedPart1Length > 0) {
            finalBase = part1.substring(0, allowedPart1Length) + tildePart
          } else {
            finalBase = tildePart
          }
        } else {
        // Если базовое имя короткое, но вместе с суффиксом дубликата вылезает за maxLength
          if ((baseName.length + suffix.length) > maxLength) {
            finalBase = baseName.substring(0, maxLength - suffix.length)
          }
        }

        return finalBase + fullSuffix
      }

      // 4. Дедупликация (проверка уникальности без учета регистра)
      let count = 0
      let result = generateFinalFileName(name, ext, count)

      const existingLower = existingFiles.map(f => f.toLowerCase())

      while (existingLower.includes(result.toLowerCase())) {
        count++
        result = generateFinalFileName(name, ext, count)
      }

      return result
    },
    /**
   * Нормализация имени файла архива (без ограничения длины и без обработки расширения).
   * Убирает недопустимые для ОС символы, чтобы имя корректно сохранилось при скачивании.
   * @param {string} name - Входящее имя архива
   * @returns {string}
   */
    $_normalizeArchiveName(name) {
      if (!name) { return 'archive' }

      const invalidAndSpaces = new RegExp('[<>:"/\\\\|?*\\s' + controlChars + ']', 'g')
      let result = name.trim()
        .replace(invalidAndSpaces, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '')
        .replace(/\.+$/, '')

      return result || 'archive'
    }
  },
}
